import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './FIRST4COM.png';
import './index.css';

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function Products() {
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Form states
  const [clientName, setClientName] = useState('');
  const [vendor, setVendor] = useState('Microsoft 365');
  const [specification, setSpecification] = useState('Basic');
  const [quantity, setQuantity] = useState(1);
  const [duration, setDuration] = useState(1);
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);

  // UI States
  const [editingId, setEditingId] = useState(null);
  const [cart, setCart] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date de lachat', direction: 'desc' });

  const vendors = ['Microsoft 365', 'Fortinet', 'VMware', 'Kaspersky', 'Veeam', 'Hébergement & Nom de domaine'];
  
  const getPlansForVendor = (v) => {
    switch (v) {
      case 'Microsoft 365': return ['Basic', 'Standard', 'Premium', 'E3', 'E5', 'Exchange', 'Teams'];
      case 'Fortinet': return ['FortiGate', 'FortiAP', 'FortiSwitch', 'FortiManager', 'FortiAnalyzer', 'Hardware', 'VM', 'Support 24x7'];
      case 'VMware': return ['vCenter', 'Foundation', 'vSphere Standard', 'vSphere Enterprise Plus', 'vSAN'];
      case 'Kaspersky': return ['Internet Security', 'Total Security', 'Select', 'Advanced'];
      case 'Veeam': return ['Business Cloud', 'Backup & Replication', 'Veeam ONE', 'Availability Suite'];
      case 'Hébergement & Nom de domaine': return ['.com', '.net', '.ma', '.org', '.fr', 'Pack Perso', 'Pack Pro', 'Pack Business', 'VPS', 'Serveur Dédié'];
      default: return ['Standard'];
    }
  };

  useEffect(() => { fetchPurchases(); }, []);
  useEffect(() => { if (!editingId) setSpecification(getPlansForVendor(vendor)[0]); }, [vendor, editingId]);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/products`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setPurchases(data);
        setError('');
      }
    } catch (err) { setError('Erreur de chargement des données.'); }
    finally { setIsLoading(false); }
  };


  const calculateEndDate = (startDateStr, durationYears) => {
    if (!startDateStr) return null;
    const date = new Date(startDateStr);
    date.setFullYear(date.getFullYear() + (parseInt(durationYears, 10) || 1));
    return date;
  };

  const stats = useMemo(() => {
    const today = new Date();
    const thirtyDaysLater = new Date();
    thirtyDaysLater.setDate(today.getDate() + 30);
    const expiringSoon = purchases.filter(p => {
      const end = calculateEndDate(p.date_achat, p.duree);
      return end && end > today && end <= thirtyDaysLater;
    }).length;
    const totalClients = new Set(purchases.map(p => p.nom_client)).size;
    return { total: purchases.length, clients: totalClients, urgent: expiringSoon };
  }, [purchases]);


  const addItemToCart = (e) => {
    e.preventDefault();
    if (!clientName.trim() || !vendor || !quantity || !purchaseDate) return;
    setCart([...cart, { id: Date.now(), clientName, vendor, specification, quantity: parseInt(quantity, 10), duration: parseInt(duration, 10), date: purchaseDate }]);
  };

  const saveAllPurchases = async () => {
    if (cart.length === 0) return;
    setIsLoading(true);
    try {
      for (const item of cart) {
        await fetch(`${API_URL}/api/products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nom_client: item.clientName, nom_produit: item.vendor, specification: item.specification, quantiter: item.quantity, date_achat: item.date, duree: item.duration })
        });
      }
      setCart([]); setClientName(''); setError(''); fetchPurchases();
    } catch (err) { setError('Erreur lors de la sauvegarde.'); }
    finally { setIsLoading(false); }
  };

  const deletePurchase = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette licence ?')) return;
    setIsLoading(true);
    try {
      await fetch(`${API_URL}/api/products/${id}`, { method: 'DELETE' });
      fetchPurchases();
    } catch (err) { setError('Erreur lors de la suppression.'); }
    finally { setIsLoading(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.id); setClientName(p.nom_client); setVendor(p.nom_produit);
    setSpecification(p.specification); setQuantity(p.quantiter); setDuration(p.duree || 1);
    setPurchaseDate(p.date_achat);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveUpdate = async () => {
    setIsLoading(true);
    try {
      await fetch(`${API_URL}/api/products/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom_client: clientName, nom_produit: vendor, specification, quantiter: parseInt(quantity, 10), date_achat: purchaseDate, duree: parseInt(duration, 10) })
      });
      setEditingId(null); setClientName(''); setError(''); fetchPurchases();
    } catch (err) { setError('Erreur lors de la mise à jour.'); }
    finally { setIsLoading(false); }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFiltered = useMemo(() => {
    let items = purchases.filter(p => (p.nom_client || "").toLowerCase().includes(filterName.toLowerCase()) && (filterDate === '' || (p.date_achat && p.date_achat.substring(0, 10) === filterDate)));
    items.sort((a, b) => {
      let aV = a[sortConfig.key], bV = b[sortConfig.key];
      if (sortConfig.key === 'expiration') { aV = calculateEndDate(a.date_achat, a.duree); bV = calculateEndDate(b.date_achat, b.duree); }
      if (aV < bV) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aV > bV) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [purchases, filterName, filterDate, sortConfig]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getBadgeClass = (v) => {
    if (!v) return 'badge-web';
    if (v.includes('Microsoft')) return 'badge-microsoft';
    if (v.includes('Fortinet')) return 'badge-fortinet';
    if (v.includes('VMware')) return 'badge-vmware';
    return 'badge-web';
  };

  return (
    <div className="dashboard-container">
      {isLoading && <div className="global-loader-overlay"><div className="loader"></div></div>}
      <header className="dashboard-header">
        <div className="header-left">
          <img src={logo} alt="First4Com" className="main-logo-small" />
          <div className="v-divider"></div>
          <h1>DASHBOARD</h1>
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/users')} className="primary-btn secondary">👤 Gérer Utilisateurs</button>
          <button onClick={() => navigate('/')} className="primary-btn logout">Déconnexion</button>
        </div>
      </header>

      {error && (
        <div className="error-banner" style={{marginBottom: '2rem'}}>
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Total Licences</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Clients Uniques</span>
          <span className="stat-value">{stats.clients}</span>
        </div>
        <div className="stat-card urgent">
          <span className="stat-label">🚨 Expirent sous 30j</span>
          <span className="stat-value">{stats.urgent}</span>
        </div>
      </div>

      <div className="dashboard-content">
        <aside className="form-box">
          <h2>{editingId ? '✍️ Modifier la Licence' : '➕ Ajouter une Licence'}</h2>
          <div className="input-group">
            <label>Nom du Client</label>
            <input type="text" placeholder="ex: Entreprise SARL" value={clientName} onChange={(e) => setClientName(e.target.value)} />
          </div>
          <div className="input-group">
            <label>Fournisseur</label>
            <select value={vendor} onChange={(e) => setVendor(e.target.value)}>
              {vendors.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label>Spécification / Plan</label>
            <select value={specification} onChange={(e) => setSpecification(e.target.value)}>
              {getPlansForVendor(vendor).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="input-group">
              <label>Quantité</label>
              <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Durée (ans)</label>
              <input type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} />
            </div>
          </div>
          <div className="input-group">
            <label>Date de début</label>
            <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
          </div>

          {editingId ? (
            <div className="btn-group">
              <button onClick={saveUpdate} className="primary-btn">Mettre à jour</button>
              <button onClick={() => {setEditingId(null); setClientName('');}} className="primary-btn ghost">Annuler</button>
            </div>
          ) : (
            <>
              <button onClick={addItemToCart} className="primary-btn action">Ajouter au Dossier</button>
              {cart.length > 0 && (
                <div className="cart-mini-list">
                  <p className="cart-title">ÉLÉMENTS À AJOUTER :</p>
                  <ul>
                    {cart.map(i => <li key={i.id}>🔹 {i.vendor} ({i.specification}) x{i.quantity}</li>)}
                  </ul>
                  <button onClick={saveAllPurchases} className="primary-btn full">Confirmer le Dossier</button>
                </div>
              )}
            </>
          )}
        </aside>

        <main className="table-box">
          <div className="table-header">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <div style={{width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)'}}></div>
              <h2>Base de Données Licences</h2>
            </div>
            <div className="search-bar">
              <input type="text" placeholder="Filtrer par client..." className="search-input" value={filterName} onChange={e => setFilterName(e.target.value)} />
              <input type="date" className="search-input" value={filterDate} onChange={e => setFilterDate(e.target.value)} />
            </div>
          </div>
          <div style={{overflowX: 'auto'}}>
            <table className="purchases-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('nom client')} className="sortable-th">Client {getSortIcon('nom client')}</th>
                  <th>Produit / Plan</th>
                  <th>Qté</th>
                  <th onClick={() => requestSort('date de lachat')} className="sortable-th">Date Début {getSortIcon('date de lachat')}</th>
                  <th onClick={() => requestSort('expiration')} className="sortable-th">Expiration {getSortIcon('expiration')}</th>
                  <th style={{textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFiltered.map(p => {
                  const end = calculateEndDate(p.date_achat, p.duree);
                  const isUrgent = end && (end - new Date()) / (1000*60*60*24) < 30;
                  return (
                    <tr key={p.id}>
                      <td><strong>{p.nom_client || 'Client Inconnu'}</strong></td>
                      <td>
                        <span className={`vendor-badge ${getBadgeClass(p.nom_produit)}`}>{p.nom_produit || 'Produit'}</span>
                        <div style={{fontSize:'0.75rem', marginTop:'4px', color:'var(--text-muted)'}}>{p.specification || '-'}</div>
                      </td>
                      <td>{p.quantiter || 0}</td>
                      <td>{p.date_achat && typeof p.date_achat === 'string' ? p.date_achat.substring(0, 10).split('-').reverse().join('/') : '-'}</td>
                      <td>
                        <div style={{color: isUrgent ? 'var(--error)' : 'inherit', fontWeight: isUrgent ? '700' : '400', display: 'flex', alignItems: 'center', gap: '6px'}}>
                          {isUrgent && <span style={{fontSize: '10px'}}>⏰</span>}
                          {end && !isNaN(end.getTime()) ? end.toLocaleDateString('fr-FR') : '-'}
                        </div>
                      </td>
                      <td style={{textAlign: 'right'}}>
                        <button onClick={() => startEdit(p)} className="action-btn edit-btn" title="Modifier">✏️</button>
                        <button onClick={() => deletePurchase(p.id)} className="action-btn delete-btn" title="Supprimer">🗑️</button>
                      </td>
                    </tr>
                  );
                })}
                {sortedAndFiltered.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{textAlign: 'center', padding: '3rem', color: 'var(--text-muted)'}}>Aucune licence trouvée.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Products;
